import { tokens, useAuth } from "@/utilities"
import { Box, Button, Typography, useTheme, CircularProgress } from "@mui/material"
import { ChangeEvent, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import * as XLSX from 'xlsx';
import { convertProgramObject } from ".";
import facultyService from "@/api/faculty.service";
import { facultyProps } from "@/types";
import careersService from "@/api/careers.service";
import Swal from "sweetalert2";

const requiredFields = [
  "CÓDIGO_SNIES_PROGRAMA",
  "NOMBRE_DEL_PROGRAMA",
  "NIVEL_FORMACIÓN",
  "NIVEL_FORMACIÓN_DETALLADO_SEGÚN_MINISTERIO",
  "PRECIO_PROGRAMA_POR_PERIODO",
  "TIPO_DE_MONEDA",
  "MODALIDAD",
  "TIPO_HORARIO",
  "NÚMERO_CRÉDITOS_PROGRAMA",
  "NÚMERO_PERIODOS_DURACIÓN",
  "TIPO_PERIODO",
  "DESCRIPCIÓN_PROGRAMA",
  "TÍTULO_OTORGADO",
  "PERFIL_DEL_GRADUADO"
];

function validateProgramObject(programObject: any) {
  for (const requiredField of requiredFields) {
    if (!(requiredField in programObject) || programObject[requiredField] === undefined || programObject[requiredField] === null || String(programObject[requiredField]).trim() === "") {
      return false;
    }
  }

  return true;
}

function getErrorMessage(programObject: any) {
  let errorMessage = "La fila no es válida porque falta: ";

  for (const requiredField of requiredFields) {
    if (!(requiredField in programObject) || programObject[requiredField] === undefined || programObject[requiredField] === null || String(programObject[requiredField]).trim() === "") {
      errorMessage += requiredField + ", ";
    }
  }

  errorMessage = errorMessage.slice(0, -2);

  return errorMessage;
}

interface Props {
  facultyId?: string;
}

export const FormatUploadCareers = ({ facultyId: propFacultyId }: Props) => {
  const { user, institute } = useAuth();
  const { code, id } = useParams<{ code?: string; id?: string }>();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [facultyData, setFacultyData] = useState<facultyProps | null>(null);
  const [error, setError] = useState<null | string[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const instituteName = institute?.name;
  const headquarter = institute?.headquarters;
  const role = user?.userData.role;
  const rawFacultyId = propFacultyId || id || (role === "Administrador IES" ? id : (Array.isArray(user?.userData.uidFaculty) ? user?.userData.uidFaculty[0] : user?.userData.uidFaculty));
  const facultyId = rawFacultyId || "";
  const instituteId = code || institute?.code || user?.userData?.institute || "";
  const iesCharacter = institute?.character;
  const iesImages = institute?.images;
  const iesLogo = institute?.logo;
  const facultyName = facultyData?.name;

  const handlerFile = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    try {
      if (file) {
        setLoading(true);
        const reader = new FileReader();

        reader.readAsBinaryString(file);
        reader.onload = async (e: ProgressEvent<FileReader>) => {
          try {
            const data = e.target?.result;
            const workbook = XLSX.read(data, { type: 'binary' });

            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];
            const dataObject: any[] = XLSX.utils.sheet_to_json(worksheet);

            const errorMessages: string[] = [];

            for (let i = 0; i < dataObject.length; i++) {
              const programObject = dataObject[i];
              const isValid = validateProgramObject(programObject);

              if (!isValid) {
                const errorMessage = getErrorMessage(programObject);
                errorMessages.push(`Error en la fila ${i + 2}: ${errorMessage}`);
              }
            }

            setError(errorMessages);

            if (errorMessages.length === 0 && dataObject.length > 0) {
              const instName = instituteName || "";
              const hq = headquarter || [];
              const fId = facultyId || "";
              const iId = instituteId || "";
              const char = iesCharacter || "";
              const imgs = iesImages || [];
              const logo = iesLogo || "";

              let currentFacultyData = facultyData;
              if (!currentFacultyData && fId && iId) {
                try {
                  currentFacultyData = await facultyService.getById(iId, fId);
                } catch (e) {
                  console.error("Error fetching faculty data", e);
                }
              }

              const subLvl = typeof currentFacultyData?.subscriptionLevel === "number"
                ? currentFacultyData.subscriptionLevel
                : (typeof institute?.subscriptionLevel === "number" ? institute.subscriptionLevel : 0);
              const fName = currentFacultyData?.name || facultyName || "";

              const formattedPrograms = dataObject.map((object) =>
                convertProgramObject(object, instName, hq, fId, iId, char, imgs, logo, subLvl, fName)
              );

              let successCount = 0;
              for (const career of formattedPrograms) {
                const res = await careersService.createCareerFromBulk(career, iId, fId);
                if (res) successCount++;
              }

              Swal.fire({
                title: "Carga Masiva Completada",
                text: `Se procesaron ${successCount} de ${formattedPrograms.length} carreras correctamente (creadas o actualizadas).`,
                icon: "success",
                confirmButtonText: "Entendido",
              });
            } else if (dataObject.length === 0) {
              Swal.fire({
                title: "Archivo Vacío",
                text: "El archivo Excel seleccionado no contiene filas de datos.",
                icon: "warning",
              });
            }
          } catch (err) {
            console.error("Error al procesar el archivo Excel:", err);
            Swal.fire({
              title: "Error al procesar archivo",
              text: "Ocurrió un error inesperado al leer la información del Excel.",
              icon: "error",
            });
          } finally {
            setLoading(false);
            event.target.value = "";
          }
        };
      }
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  };

  useEffect(() => {
    async function fetchFaculties() {
      if (facultyId && instituteId) {
        try {
          const getFaculties = await facultyService.getById(instituteId, facultyId);
          setFacultyData(getFaculties);
        } catch (error) {
          console.error("Error trying to get faculties", error);
        }
      }
    }
    fetchFaculties();
  }, [facultyId, instituteId]);

  return (
    <Box sx={{ width: "100%", display: "flex" }}>
      <Box sx={{ width: "65%", padding: "40px" }}>
        <Typography variant="h3" color={colors.grey[100]} sx={{ marginBottom: "15px", fontWeight: "bold" }}>
          Guía Práctica para el Registro Masivo de Programas Académicos
        </Typography>
        <Typography variant="h5" color={colors.grey[100]} sx={{ marginBottom: "15px" }}>
          ¡Estimado equipo! Para facilitar y agilizar la creación y actualización de las carreras en la plataforma, se ha diseñado el formato en Excel (Formato_Programas_v2.xlsx) que les permitirá realizar cargas masivas.
        </Typography>
        <Typography variant="h5" color={colors.grey[100]} sx={{ marginBottom: "15px" }}>
          Antes de comenzar, los invitamos a revisar los siguientes pasos clave que asegurarán el éxito de su registro:
        </Typography>

        <ol style={{ listStyleType: "none", color: colors.grey[100] }}>
          <li style={{ marginBottom: "15px" }}>
            <Typography variant="h4" color={colors.grey[100]} sx={{ fontWeight: "bold", marginBottom: "5px" }}>
              1. Diligenciamiento de Campos Obligatorios
            </Typography>
            <ul style={{ paddingLeft: "20px" }}>
              <li>
                <Typography variant="h5" color={colors.grey[100]}>
                  <b>Campos Obligatorios al inicio:</b> Las primeras 14 columnas corresponden a los campos obligatorios del registro (Código SNIES, Nombre, Nivel de estudio, Nivel de formación detallado según ministerio, Precio, Moneda, Modalidad, Horario, Créditos, Duración, Tipo de periodo, Descripción, Título otorgado y Perfil del graduado).
                </Typography>
              </li>
              <li>
                <Typography variant="h5" color={colors.grey[100]}>
                  <b>Código SNIES:</b> Identificador único del programa. Si la carrera ya existe en el sistema, se actualizará; de lo contrario, se creará.
                </Typography>
              </li>
              <li>
                <Typography variant="h5" color={colors.grey[100]}>
                  <b>Cálculo automático:</b> El campo <i>Nivel Académico</i> (Pregrado/Posgrado) se calcula automáticamente según el Nivel de Estudio seleccionado.
                </Typography>
              </li>
            </ul>
          </li>

          <li style={{ marginBottom: "15px" }}>
            <Typography variant="h4" color={colors.grey[100]} sx={{ fontWeight: "bold", marginBottom: "5px" }}>
              2. Formatos Específicos y Listas Desplegables
            </Typography>
            <ul style={{ paddingLeft: "20px" }}>
              <li>
                <Typography variant="h5" color={colors.grey[100]}>
                  <b>Valores predefinidos:</b> En la hoja "Opciones" del Excel encontrarán las alternativas válidas para Nivel de estudio, Nivel de formación detallado según ministerio, Modalidad, Horario, Periodo y Moneda.
                </Typography>
              </li>
              <li>
                <Typography variant="h5" color={colors.grey[100]}>
                  <b>Formato de Fechas:</b> Para FECHA_INICIO_PROGRAMA, PRÓXIMAS_INSCRIPCIONES e INICIO_DE_CLASES, utilicen la estructura Día/Mes/Año (Ejemplo: 01/01/2026).
                </Typography>
              </li>
            </ul>
          </li>

          <li style={{ marginBottom: "15px" }}>
            <Typography variant="h4" color={colors.grey[100]} sx={{ fontWeight: "bold", marginBottom: "5px" }}>
              3. Calidad del Contenido y Conexión Institucional
            </Typography>
            <ul style={{ paddingLeft: "20px" }}>
              <li>
                <Typography variant="h5" color={colors.grey[100]}>
                  <b>Excelencia en la redacción:</b> Cuiden la gramática y ortografía en las descripciones y perfiles para proyectar la calidad institucional.
                </Typography>
              </li>
            </ul>
          </li>
        </ol>

        <Typography variant="h4" color={colors.grey[100]} sx={{ fontWeight: "bold", marginTop: "20px", marginBottom: "5px" }}>
          ¿Desean gestionar un solo programa?
        </Typography>
        <ul style={{ paddingLeft: "20px", color: colors.grey[100] }}>
          <li>
            <Typography variant="h5" color={colors.grey[100]}>
              <b>Para crear una nueva carrera:</b> Pueden hacerlo desde la pestaña "Crear carrera".
            </Typography>
          </li>
          <li>
            <Typography variant="h5" color={colors.grey[100]}>
              <b>Para actualizar un programa existente:</b> Pueden editarlo directamente en las pestañas "Activas" o "Inactivas" o realizar la carga masiva mediante el código SNIES.
            </Typography>
          </li>
        </ul>
      </Box>

      <Box sx={{ width: "30%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "space-evenly" }}>
        <Button variant="outlined" href="/files/Formato_Programas_v2.xlsx" download="Formato_Programas_v2.xlsx">
          Descargar Formato
        </Button>

        <Box>
          <label htmlFor="format" style={{ position: "relative" }}>
            <input
              accept=".xlsx, .xls"
              id="format"
              type="file"
              style={{ display: 'none' }}
              onChange={handlerFile}
              disabled={loading}
            />
            <Button variant="outlined" component="span" disabled={loading}>
              {loading ? <CircularProgress size={24} /> : "Subir Archivo"}
            </Button>
          </label>
        </Box>
        {error && error.length > 0 &&
          <ul style={{ maxHeight: "200px", overflowY: "auto", paddingLeft: "15px" }}>
            {error.map((errorMessage, index) => (
              <li key={index} style={{ color: "red" }}>
                <Typography color={"red"} variant="caption">{errorMessage}</Typography>
              </li>
            ))}
          </ul>
        }
      </Box>
    </Box>
  );
};
